import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { fetchDiscoverSummary, type DiscoverSummary } from "@/api/discover";
import { deriveLeadStage } from "@/components/booking/LeadsKanbanBoard";
import { useBookingLeads } from "@/hooks/booking/useBookingLeads";
import { useManagerEvents } from "@/hooks/useManagerEvents";
import { useOnboardingProgress } from "@/hooks/useOnboardingProgress";
import { useArtistScope } from "@/lib/artistContext";
import { mapBookingLeadToCrmLead } from "@/lib/booking";

/* ------------------------------------------------------------------ */
/*  Home: "What should I do next?"                                      */
/*  Database-only reads. No AI, no collection, no external providers.   */
/* ------------------------------------------------------------------ */

// MVP activation steps. Email/calendar are optional integrations, not onboarding.
const ONBOARDING_STEPS = ["account", "artist", "goals", "comparables"] as const;
type OnboardingStepKey = (typeof ONBOARDING_STEPS)[number];

const STEP_LABELS: Record<OnboardingStepKey, string> = {
  account: "Create account",
  artist: "Add your artist",
  goals: "Choose your goals",
  comparables: "Pick comparable artists",
};

const STEP_PATHS: Record<OnboardingStepKey, string> = {
  account: "/signup/account",
  artist: "/signup/artist",
  goals: "/signup/goals",
  comparables: "/signup/comparables",
};

/** Legacy stored steps map onto the activation journey. */
function toStepIndex(step: string | null | undefined): number {
  if (step === "organization") return 1;
  if (step === "email" || step === "calendar") return 2;
  return ONBOARDING_STEPS.indexOf((step ?? "account") as OnboardingStepKey);
}

type NextAction = { label: string; description: string; to: string };

function StatTile({ label, value, loading, to }: { label: string; value: number; loading: boolean; to: string }) {
  return (
    <Link
      to={to}
      className="aa-glass-card block rounded-2xl p-4 transition-colors hover:border-primary/40"
      aria-label={`${label}: ${loading ? "loading" : value}`}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      {loading ? (
        <div className="mt-1 h-8 w-12 animate-pulse rounded-lg bg-muted/60" />
      ) : (
        <p className="tabular-nums text-3xl font-bold text-foreground">{value}</p>
      )}
    </Link>
  );
}

export default function Dashboard() {
  const { selectedArtistId, artists } = useArtistScope();
  const artistId = selectedArtistId || null;
  const artistName = artists?.find((artist) => artist.id === artistId)?.name ?? null;

  const [summary, setSummary] = useState<DiscoverSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(false);

  const { leads, loading: leadsLoading } = useBookingLeads({ status: "pending,confirmed", limit: 200 });
  const { upcomingEvents, loading: eventsLoading } = useManagerEvents(200);
  const { status: onboardingStatus } = useOnboardingProgress();

  useEffect(() => {
    if (!artistId) return;
    let cancelled = false;
    setSummaryLoading(true);
    setSummaryError(false);
    fetchDiscoverSummary(artistId)
      .then((payload) => {
        if (!cancelled) setSummary(payload);
      })
      .catch(() => {
        if (!cancelled) setSummaryError(true);
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [artistId]);

  const stageCounts = useMemo(() => {
    const counts = { prospecting: 0, outreach: 0, negotiation: 0, completed: 0 };
    leads.forEach((record) => {
      const stage = deriveLeadStage(mapBookingLeadToCrmLead(record));
      if (stage in counts) counts[stage as keyof typeof counts] += 1;
    });
    return counts;
  }, [leads]);

  const nextShows = upcomingEvents.slice(0, 4);

  const discoverMessage = useMemo(() => {
    if (!summary) return null;
    if (summary.comparable_count === 0) {
      return {
        text: "Choose comparable artists to start discovering venues.",
        cta: "Choose comparable artists",
        to: "/discover",
      };
    }
    if (summary.venue_count === 0) {
      return {
        text: "Find where your comparable artists play.",
        cta: "Find their venues",
        to: "/discover",
      };
    }
    return {
      text: `${summary.venue_count} venue${summary.venue_count === 1 ? "" : "s"} found from ${
        summary.comparable_count
      } comparable artist${summary.comparable_count === 1 ? "" : "s"}.`,
      cta: "Explore venues",
      to: "/discover?tab=venues",
    };
  }, [summary]);

  const nextActions = useMemo<NextAction[]>(() => {
    const actions: NextAction[] = [];
    if (summary && summary.comparable_count === 0) {
      actions.push({ label: "Choose comparable artists", description: "Tell us whose live history to learn from.", to: "/discover" });
    } else if (summary && summary.venue_count === 0) {
      actions.push({ label: "Find venues", description: "Load where your comparable artists have played.", to: "/discover" });
    } else if (summary && summary.saved_venue_count > 0) {
      actions.push({
        label: "Pitch a saved venue",
        description: `You have ${summary.saved_venue_count} saved venue${summary.saved_venue_count === 1 ? "" : "s"} ready to pitch.`,
        to: "/discover",
      });
    } else if (summary) {
      actions.push({ label: "Find venues", description: "Browse venues your comparable artists have played.", to: "/discover" });
    }
    if (stageCounts.prospecting > 0) {
      actions.push({
        label: "Continue pitching prospects",
        description: `${stageCounts.prospecting} prospect${stageCounts.prospecting === 1 ? "" : "s"} not contacted yet.`,
        to: "/booking",
      });
    }
    if (stageCounts.outreach > 0) {
      actions.push({
        label: "Review outreach",
        description: `${stageCounts.outreach} venue${stageCounts.outreach === 1 ? "" : "s"} contacted and awaiting a reply.`,
        to: "/booking",
      });
    }
    if (nextShows.length > 0) {
      actions.push({ label: "View upcoming shows", description: "See what's on the calendar.", to: "/shows" });
    }
    return actions;
  }, [summary, stageCounts, nextShows.length]);

  const showOnboarding = onboardingStatus !== null && !onboardingStatus.onboarding_completed;
  const currentStepIdx = onboardingStatus ? toStepIndex(onboardingStatus.current_step) : -1;

  if (!artistId) {
    return (
      <div className="aa-page-shell space-y-4">
        <h1 className="aa-page-title text-3xl font-semibold">Home</h1>
        <div className="aa-glass-card rounded-2xl p-6 text-center text-sm text-muted-foreground">
          Select an artist to get started.
        </div>
      </div>
    );
  }

  return (
    <div className="aa-page-shell space-y-4">
      <div>
        <h1 className="aa-page-title text-3xl font-semibold">Home</h1>
        <p className="text-sm text-muted-foreground">
          {artistName ? `What's next for ${artistName}.` : "What's next for your live career."}
        </p>
      </div>

      {/* Discover entry */}
      <section className="aa-glass-card rounded-2xl p-4" aria-label="Discover">
        <h2 className="text-lg font-semibold text-foreground">Discover</h2>
        {summaryLoading && <p className="mt-1 text-sm text-muted-foreground">Loading…</p>}
        {!summaryLoading && summaryError && (
          <p className="mt-1 text-sm text-muted-foreground">We couldn't load your Discover summary right now.</p>
        )}
        {!summaryLoading && discoverMessage && (
          <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground">{discoverMessage.text}</p>
            <Link
              to={discoverMessage.to}
              className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              {discoverMessage.cta}
            </Link>
          </div>
        )}
      </section>

      {/* Booking snapshot */}
      <section aria-label="Booking snapshot" className="space-y-2">
        <h2 className="text-lg font-semibold text-foreground">Booking snapshot</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label="Saved venues" value={summary?.saved_venue_count ?? 0} loading={summaryLoading} to="/discover" />
          <StatTile label="Prospects" value={stageCounts.prospecting} loading={leadsLoading} to="/booking" />
          <StatTile label="Outreach" value={stageCounts.outreach} loading={leadsLoading} to="/booking" />
          <StatTile label="Upcoming shows" value={upcomingEvents.length} loading={eventsLoading} to="/shows" />
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Next actions */}
        <section className="aa-glass-card rounded-2xl p-4" aria-label="Next actions">
          <h2 className="mb-3 text-lg font-semibold text-foreground">Next actions</h2>
          {nextActions.length === 0 ? (
            <p className="text-sm text-muted-foreground">You're all caught up.</p>
          ) : (
            <ul className="space-y-2">
              {nextActions.map((action) => (
                <li key={action.label}>
                  <Link
                    to={action.to}
                    className="block rounded-xl border border-border/60 bg-background/40 px-3 py-2 transition-colors hover:border-primary/40"
                  >
                    <p className="text-sm font-medium text-foreground">{action.label}</p>
                    <p className="text-xs text-muted-foreground">{action.description}</p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Upcoming shows */}
        <section className="aa-glass-card rounded-2xl p-4" aria-label="Upcoming shows">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">Upcoming shows</h2>
            <Link to="/shows" className="text-xs text-primary hover:underline">
              All shows →
            </Link>
          </div>
          {eventsLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : nextShows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No upcoming shows yet.</p>
          ) : (
            <ul className="space-y-2">
              {nextShows.map((show, i) => (
                <li key={show.id ?? i} className="flex items-start gap-3 border-b border-border/60 pb-2 last:border-0">
                  <div className="mt-0.5 w-10 shrink-0 text-center">
                    {show.event_date ? (
                      <>
                        <p className="text-[10px] font-bold uppercase leading-none text-primary">
                          {new Date(show.event_date).toLocaleDateString("en-US", { month: "short" })}
                        </p>
                        <p className="tabular-nums text-xl font-bold leading-tight text-foreground">
                          {new Date(show.event_date).getDate()}
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground">TBD</p>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{show.venue ?? show.name ?? "Venue TBD"}</p>
                    <p className="text-xs text-muted-foreground">
                      {[show.city, show.state].filter(Boolean).join(", ") || "Location TBD"}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Getting started (only while onboarding is incomplete) */}
      {showOnboarding && (
        <section className="aa-glass-card rounded-2xl p-4" aria-label="Getting started">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">Getting started</h2>
            <span className="text-xs text-muted-foreground">
              {Math.max(currentStepIdx, 0)} of {ONBOARDING_STEPS.length} complete
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {ONBOARDING_STEPS.map((step, i) => {
              const done = currentStepIdx >= 0 && i < currentStepIdx;
              const current = i === currentStepIdx;
              return (
                <Link
                  key={step}
                  to={STEP_PATHS[step]}
                  className={`rounded-xl px-3 py-2 text-sm transition-colors ${
                    done
                      ? "bg-emerald-500/10 text-emerald-600"
                      : current
                        ? "border border-primary/30 bg-primary/5 font-medium text-primary"
                        : "bg-background/50 text-muted-foreground"
                  }`}
                >
                  {done ? "✓ " : ""}
                  {STEP_LABELS[step]}
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
